import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    Avatar
} from '@mui/material';
import { Search as SearchIcon, History as HistoryIcon, Person as PersonIcon } from '@mui/icons-material';
import { useAdminLogs } from 'hooks/useLogs';
import { format } from 'date-fns';

export default function AuditLogList() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');

    const { data: logsRes, isLoading } = useAdminLogs({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined
    });

    const logs = Array.isArray(logsRes) ? logsRes : logsRes?.data || [];
    const totalLogs = logsRes?.total || 0;

    const getRoleColor = (role) => {
        switch (role?.toLowerCase()) {
            case 'admin':
                return 'primary';
            case 'staff':
                return 'secondary';
            default:
                return 'default';
        }
    };

    return (
        <Box p={3} sx={{ background: '#f5f6fa', minHeight: '100vh' }}>
            <Box mb={4}>
                <Typography variant="h4" fontWeight={700}>
                    System Audit Logs
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Track all administrative actions and security events on the platform
                </Typography>
            </Box>

            <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                <Box p={3}>
                    <TextField
                        placeholder="Search by action description..."
                        size="small"
                        fullWidth
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ maxWidth: 400 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <TableContainer>
                    <Table>
                        <TableHead sx={{ bgcolor: '#f8f9fa' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600, pl: 3 }}>USER</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>ROLE</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>ACTION</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>DATE & TIME</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" py={5}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : logs.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" py={5}>
                                        <HistoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                                        <Typography variant="body2" color="text.secondary">
                                            No audit logs found.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                logs.map((log) => (
                                    <TableRow key={log._id} hover>
                                        <TableCell sx={{ pl: 3 }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                                                    <PersonIcon fontSize="small" color="primary" />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={700}>
                                                        {log.userId?.firstName} {log.userId?.lastName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {log.userId?.email}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={log.userId?.role?.toUpperCase()}
                                                size="small"
                                                color={getRoleColor(log.userId?.role)}
                                                sx={{ fontWeight: 700, fontSize: '0.65rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {log.action}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {log.createdAt ? format(new Date(log.createdAt), 'MMM dd, yyyy') : '---'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {log.createdAt ? format(new Date(log.createdAt), 'HH:mm:ss') : '---'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalLogs}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
}
